import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { styled, Tooltip as MuiTooltip, tooltipClasses } from '@mui/material';

export const DefaultTooltipPopperProps = {
  modifiers: [
    {
      flip: { enabled: true },
      preventOverflow: { enabled: true, boundariesElement: 'viewport' },
    },
  ],
};

const StyledTooltipLight = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    minWidth: 1000,
    backgroundColor: '#FFFFFF',
  },
  [`& h3, & span, & div`]: {
    margin: 0,
    padding: 0,
    fontSize: 14,
    fontWeight: 400,
    color: '#2F3037',
  },
}));

const StyledTooltipDark = styled(({ className, ...props }) => (
  <MuiTooltip children={null} componentsProps={{ tooltip: { className: className } }} {...props} />
))(`
    padding: 12px;
    font-size: 10px;
    font-weight: 400;
    border-radius: 4px;
    line-height: 16px;
    color: #FFFFFF;
    background-color: #2F3037;
`);

const StyledTooltipDefault = MuiTooltip;

const StyledTooltipFieldInfo = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FFFFFF',
    padding: 12,
    borderRadius: 4,
    border: '1px solid #9293A0',
    boxShadow: 'rgba(0, 0, 0, 0.10) 0px 4px 12px 1px',
  },
  [`& h3, & span, & div`]: {
    margin: 0,
    padding: 0,
    fontSize: 14,
    fontWeight: 400,
    color: '#2F3037',
  },
}));

const StyledTooltipGeneral = styled(({ className, style, mode = 'dark', ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} style={style} />
))(({ mode }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: mode === 'light' ? '#fff' : 'rgba(0, 0, 0, 0.9)',
    color: mode === 'light' ? '#2F3037' : 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    lineHeight: 1.4,
    whiteSpace: 'normal',
    boxShadow: mode === 'light' ? '0 4px 16px rgba(0,0,0,0.10)' : 'none',
    minWidth: 0,
    fontWeight: 400,
    fontFamily: 'inherit',
    maxWidth: 'inherit',
    border: 'none',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: mode === 'light' ? '#fff' : 'rgba(0, 0, 0, 0.9)',
    marginTop: '-1px',
    border: 'none',
    boxShadow: mode === 'light' ? '0 4px 16px rgba(0,0,0,0.10)' : 'none',
  },
  [`& .tooltip-content`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  [`& .tooltip-title`]: {
    fontWeight: 500,
    margin: 0,
    padding: 0,
  },
  [`& .tooltip-description`]: {
    fontSize: 12,
    opacity: 0.8,
    whiteSpace: 'normal',
    margin: 0,
    padding: 0,
  },
}));

// forwardRef is to prevent the console warning when Tooltip is used as a direct
// child of material-ui Menu in ConfigureRecipe
const Tooltip = forwardRef(
  ({ tooltipTheme = 'dark', rootStyle = {}, placement, children, ...otherProps }, _ref) => {
    let TooltipConstructor;

    switch (tooltipTheme) {
      case 'fieldInfo':
        TooltipConstructor = StyledTooltipFieldInfo;
        break;
      case 'light':
        TooltipConstructor = StyledTooltipLight;
        break;
      case 'dark':
        TooltipConstructor = StyledTooltipDark;
        break;
      case 'general':
        TooltipConstructor = StyledTooltipGeneral;
        break;
      default:
        TooltipConstructor = StyledTooltipDefault;
    }

    return (
      <TooltipConstructor
        enterDelay={300}
        leaveDelay={300}
        placement={placement}
        style={rootStyle}
        {...otherProps}
      >
        {children}
      </TooltipConstructor>
    );
  },
);

Tooltip.propTypes = {
  tooltipTheme: PropTypes.string,
  rootStyle: PropTypes.object,
  placement: PropTypes.string,
};

export default Tooltip;
